import React, { useEffect, useState } from 'react';
import { Spin, Table, Input, Button } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch, type RootState } from '../../../redux/store';
// import {
//   getUnapprovedPaymentsAction,
//   updateUnsettledPaymentsAction,
// } from '../../Loan/store/loanActions';
import {
  getUnsettledPledgePaymentAction,
  updatePledgePaymentAction,
  updatePledgePaymentSplitAction,
} from '../../Pledge/store/pledgeActions';
import { type PledgePayment } from '../../Pledge/store/pledgeTypes';

export default function PledgePaymentList(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const { unsettledPledgePayments }: any = useSelector<RootState>(
    (rootState) => rootState.pledge
  );

  const [disabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [onlinePrincipalValue, setOnlinePrincipalValue] = useState<string>('');
  const [offlinePrincipalValue, setOfflinePrincipalValue] = useState<string>('');
  const [onlineInterestValue, setOnlineInterestValue] = useState<string>('');
  const [offlineInterestValue, setOfflineInterestValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const clearEdit = () => {
    setEditingId(null);
    setOnlinePrincipalValue('');
    setOfflinePrincipalValue('');
    setOnlineInterestValue('');
    setOfflineInterestValue('');
  };

  const getUnsettledPledgeList = async (): Promise<void> => {
    setIsLoading(true);
    await getUnsettledPledgePaymentAction(dispatch, {});
    setIsLoading(false);
    setSaving(false);
    clearEdit();
  };

  useEffect(() => {
    void getUnsettledPledgeList();
  }, []);

  const handleUpdate = async (pledgePayment: PledgePayment, status: string) => {
    // setDisabled(true);
    await updatePledgePaymentAction(dispatch, {
      id: pledgePayment._id ?? '',
      status,
    });
    void getUnsettledPledgeList();
    setDisabled(false);
  };

  const handleEdit = (record: PledgePayment) => {
    setEditingId(record._id ?? '');
    setOnlinePrincipalValue(String(record.onlinePrincipal ?? ''));
    setOfflinePrincipalValue(String(record.offlinePrincipal ?? ''));
    setOnlineInterestValue(String(record.onlineInterest ?? ''));
    setOfflineInterestValue(String(record.offlineInterest ?? ''));
  };

  // Both splits must reconcile to the totals the payment was raised with
  const isPrincipalSplitValid = (record: PledgePayment) =>
    Math.abs(
      Number(onlinePrincipalValue) +
        Number(offlinePrincipalValue) -
        Number(record.principal ?? 0)
    ) <= 0.009;

  const isInterestSplitValid = (record: PledgePayment) =>
    Math.abs(
      Number(onlineInterestValue) +
        Number(offlineInterestValue) -
        Number(record.interestAmount ?? 0)
    ) <= 0.009;

  const handleSave = async (record: PledgePayment) => {
    if (!isPrincipalSplitValid(record)) {
      alert('Online Principal + Offline Principal must match Principal amount');
      return;
    }
    if (!isInterestSplitValid(record)) {
      alert('Online Interest + Offline Interest must match Interest Amount');
      return;
    }

    setSaving(true);
    await updatePledgePaymentSplitAction(dispatch, {
      id: record._id ?? '',
      onlinePrincipal: Number(onlinePrincipalValue),
      offlinePrincipal: Number(offlinePrincipalValue),
      onlineInterest: Number(onlineInterestValue),
      offlineInterest: Number(offlineInterestValue),
    });
    await getUnsettledPledgeList();
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: unknown, record: PledgePayment, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      width: 150,
      render: (_: unknown, record: PledgePayment, index: number) => (
        <p>{record.branch?.name}</p>
      ),
    },
    {
      title: 'Pledge Number',
      dataIndex: 'pledgeNumber',
      key: 'pledgeNumber',
      render: (_: unknown, record: PledgePayment, index: number) => (
        <p>{record.pledgeNumber}</p>
      ),
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      render: (_: unknown, record: PledgePayment, index: number) => (
        <p>{record.principal}</p>
      ),
    },
    {
      title: 'Online Principal',
      dataIndex: 'onlinePrincipal',
      key: 'onlinePrincipal',
      render: (_: unknown, record: PledgePayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlinePrincipalValue}
            onChange={(e) => {
              setOnlinePrincipalValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlinePrincipal}</p>
        ),
    },
    {
      title: 'Offline Principal',
      dataIndex: 'offlinePrincipal',
      key: 'offlinePrincipal',
      render: (_: unknown, record: PledgePayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlinePrincipalValue}
            onChange={(e) => {
              setOfflinePrincipalValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlinePrincipal}</p>
        ),
    },
    {
      title: 'Interest Amount',
      dataIndex: 'interestAmount',
      key: 'interestAmount',
      render: (_: unknown, record: PledgePayment, index: number) => (
        <p>{record.interestAmount}</p>
      ),
    },
    {
      title: 'Online Interest',
      dataIndex: 'onlineInterest',
      key: 'onlineInterest',
      render: (_: unknown, record: PledgePayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlineInterestValue}
            onChange={(e) => {
              setOnlineInterestValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlineInterest}</p>
        ),
    },
    {
      title: 'Offline Interest',
      dataIndex: 'offlineInterest',
      key: 'offlineInterest',
      render: (_: unknown, record: PledgePayment) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlineInterestValue}
            onChange={(e) => {
              setOfflineInterestValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlineInterest}</p>
        ),
    },
    {
      title: 'Reducing Field',
      dataIndex: 'reducingField',
      key: 'reducingField',
      render: (_: unknown, record: PledgePayment, index: number) => (
        <p>{record.reducingField}</p>
      ),
    },
    {
      title: 'Extra',
      dataIndex: 'extra',
      key: 'extra',
      render: (_: unknown, record: PledgePayment, index: number) => (
        <p>{record.extra}</p>
      ),
    },
    {
      title: 'Edit',
      dataIndex: 'edit',
      key: 'edit',
      width: 120,
      render: (_: unknown, record: PledgePayment) =>
        editingId === record._id ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              icon={<SaveOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              loading={saving}
              disabled={
                !isPrincipalSplitValid(record) || !isInterestSplitValid(record)
              }
              onClick={() => {
                void handleSave(record);
              }}
            ></Button>
            <Button
              icon={<CloseOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              disabled={saving}
              onClick={clearEdit}
            ></Button>
          </div>
        ) : (
          <Button
            icon={<EditOutlined style={{ color: '#000' }} />}
            size="small"
            type="link"
            onClick={() => {
              handleEdit(record);
            }}
          ></Button>
        ),
    },
    {
      title: 'Accept',
      dataIndex: 'actions',
      width: 100,
      render: (_: unknown, record: PledgePayment, index: number) => {
        const acceptDisabled = disabled || editingId === record._id;
        return (
          <button
            className="search__button"
            style={{
              background: !acceptDisabled ? '#27ae60' : '#b2b2b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 24px',
              fontWeight: 700,
              cursor: !acceptDisabled ? 'pointer' : 'not-allowed',
              opacity: !acceptDisabled ? 1 : 0.7,
            }}
            disabled={acceptDisabled}
            onClick={() => {
              if (!acceptDisabled) {
                setDisabled(true);
                void handleUpdate(record, 'cash_settled');
              }
            }}
          >
            Accept
          </button>
        );
      },
    },
    {
      title: 'Reject',
      dataIndex: 'actions2',
      width: 100,
      render: (_: unknown, record: PledgePayment, index: number) => (
        <button
          className="search__button"
          style={{
            background: !disabled ? '#e74c3c' : '#b2b2b2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 24px',
            fontWeight: 700,
            cursor: !disabled ? 'pointer' : 'not-allowed',
            opacity: !disabled ? 1 : 0.7,
          }}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              setDisabled(true);
              void handleUpdate(record, 'rejected');
            }
          }}
        >
          Reject
        </button>
      ),
    },
  ];

  return (
    <div className="users__table__page">
      {!(unsettledPledgePayments.length > 0) && (
        <Spin spinning={isLoading} fullscreen />
      )}
      <div style={{ width: '100%' }}>
        <Table dataSource={unsettledPledgePayments} columns={columns} />
      </div>
    </div>
  );
}
